
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useVendorStore from "../../HomePage/useVendorStore";
import useCommonStore from "../../Common/useCommonStore";
import { PO_ORDER_TAG_MANDI_DD, STATUS_BEGIN, STATUS_DONE } from "utils/constants/others";
import { PDFDocument } from "pdf-lib";
import moment from "moment";
import { DataURItoBlob } from "../../../../helpers/helper";
import { LS_KEYS } from "utils/constants/localStorageKeys";

export default function useDDPoInvoiceFormLogic() {
    const getAllCities = useCommonStore((state) => state.getAllCities);
    const cities = useCommonStore((state) => state.cities);
    const getPurchaseOrders = useVendorStore((state) => state.getPurchaseOrders);
    const getPurchaseOrderStatus = useVendorStore(
        (state) => state.getPurchaseOrderStatus
    );
    const purchaseOrders = useVendorStore((state) => state.purchaseOrders);

    const [po, setPo] = useState("");
    const [items, setItems] = useState("");
    let { source, purchaseOrderId } = useParams();
    const [showLoader, setShowLoader] = useState(false);
    const [tonnage, setTonnage] = useState(0);
    const [showError, setShowError] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState({ is_custom_shipping_address_required: false });
    const [errorMsg, setErrorMsg] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [showPDF, setShowPDF] = useState(false);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [showFile, setShowFile] = useState(false);
    const [showImages, setShowImages] = useState(false);
    const [imageArray, setImageArray] = useState([]);
    const [pngArray, setPngArray] = useState([]);
    const [formData, setFormData] = useState({});
    const todayDate = moment(new Date()).local(true).format("YYYY-MM-DD");
    const [showPopup, setShowPopup] = useState(false);
    const [useSellerAddress, setUseSellerAddress] = useState(true);

    const navigate = useNavigate();

    useEffect(() => {
        getAllCities();
    }, []);

    useEffect(() => {
        getPurchaseOrders({
            is_item_required: true,
            purchase_order_id: purchaseOrderId,
        }, true);
    }, []);

    useEffect(() => {
        if (getPurchaseOrderStatus === STATUS_DONE && purchaseOrders) {
            setPo(purchaseOrders[0]);
        }
    }, [purchaseOrders]);

    useEffect(() => {
        if (po && po.purchase_order_items) {
            setItems(po.purchase_order_items);
        }
    }, [po]);

    useEffect(() => {
        if (getPurchaseOrderStatus === STATUS_BEGIN) {
            setShowLoader(true);
        }
        if (getPurchaseOrderStatus === STATUS_DONE) {
            setShowLoader(false);
        }
    }, [getPurchaseOrderStatus]);

    useEffect(() => {
        calculateTonnage();
    }, [items]);

    const calculateTonnage = () => {
        let total = 0;
        for (const item of items) {
            total = total + item.quantity_ordered * item.weight_per_packet;
        }
        setTonnage(total);
    };

    const closePopupHandler = () => {
        setShowPopup(false);
    };

    const setUpdateInvoiceDetails = (val, field) => {
        if (field === "InvoiceNumber") {
            setInvoiceDetails({
                ...invoiceDetails,
                invoice_number: val,
            });
        } else if (field === "InvoiceDate") {
            setInvoiceDetails({
                ...invoiceDetails,
                invoice_date: val,
            });
        } else if (field === "InvoiceAmount") {
            if (val === "" || /^(?!0\d)(\d+(?:\.\d{0,2})?)$/.test(val)) {
                setInvoiceDetails({
                    ...invoiceDetails,
                    invoice_amount: val,
                });
            }
        } else if (field === "InvoiceUrl") {
            setInvoiceDetails({
                ...invoiceDetails,
                supplier_bill_image: val,
            });
        } else if (field === "ShippingLine1") {
            setInvoiceDetails({
                ...invoiceDetails,
                shipping_address_line1: val,
            });
        } else if (field === "ShippingCity") {
            setInvoiceDetails({
                ...invoiceDetails,
                shipping_city: val,
            });
        } else if (field === "ShippingPincode") {
            if (val === "" || /^\d{0,6}$/.test(val)) {
                setInvoiceDetails({
                    ...invoiceDetails,
                    shipping_pincode: val,
                });
            }
        }
        if (
            imageArray?.length > 0 &&
            invoiceDetails.invoice_amount !== 0 &&
            invoiceDetails.invoice_number &&
            invoiceDetails.invoice_number?.length !== 0 &&
            invoiceDetails.supplier_bill_image &&
            invoiceDetails.supplier_bill_image?.length !== 0
        ) {
            navigateToInvoicePdf(invoiceDetails);
        }
        setShowError(false);
        setErrorMsg("");
    };

    const wrapperToNavigateToInvoicePdf = async (invoiceDetails) => {
        if (checkIfMissingFields(invoiceDetails)) {
            if (!invoiceDetails.invoice_date) {
                setShowError(true);
                setErrorMsg({
                    ...errorMsg,
                    invoice_date_error: "Invoice date can not be empty",
                });
            }
            if (imageArray?.length === 0 && !pdfBlob) {
                setShowError(true);
                setErrorMsg({
                    ...errorMsg,
                    invoice_image_error: "Supplier bill image is missing",
                });
                setShowToast(false);
                setShowToast(true);
            } else if (invoiceDetails.invoice_date && invoiceDetails.invoice_date > todayDate) {
                setShowError(true);
                setErrorMsg({
                    ...errorMsg,
                    invoice_image_error: "Invoice date can't be after today's date",
                });
                setShowToast(false);
                setShowToast(true);
            }
        } else {
            if (imageArray.length > 0) {
                generatePDF().then((pdf) => {
                    const fd = new FormData();
                    fd.append("file", DataURItoBlob(pdf), "Supplier-bill-image.pdf");
                    fd.append("fileType", "supplier_bill_image");
                    setFormData(fd);
                    displayInvoicePdf(DataURItoBlob(pdf));
                    setShowPDF(true);
                });
            } else {
                setShowPDF(true);
            }
        }
    };

    const checkIfMissingFields = (invoiceDetails) => {
        if (!invoiceDetails.invoice_date) {
            return true;
        }
        if (invoiceDetails.invoice_date && invoiceDetails.invoice_date > todayDate) {
            return true;
        }
        if (imageArray?.length === 0 && !pdfBlob) {
            return true;
        }
        return false;
    };

    const generatePDF = async () => {
        const pdfDoc = await PDFDocument.create();

        for (const imageData of pngArray) {
            const image = await pdfDoc.embedPng(imageData);
            const page = pdfDoc.addPage([image.width, image.height]);
            const { width, height } = page.getSize();
            page.drawImage(image, {
                x: 0,
                y: 0,
                width,
                height,
            });
        }

        const pdfDataUri = await pdfDoc.saveAsBase64({ dataUri: true });
        return pdfDataUri;
    };

    const navigateToInvoicePdf = (invoiceDetails) => {
        if (invoiceDetails.invoice_amount == 0) {
            setShowError(true);
            setErrorMsg({
                invoice_amount_error: "Invoice amount can not be 0",
            });
        } else if (!invoiceDetails.invoice_number || invoiceDetails.invoice_number?.length == 0) {
            setShowError(true);
            setErrorMsg({
                invoice_number_error: "Invoice number can not be empty",
            });
        } else if (!invoiceDetails.invoice_date) {
            setShowError(true);
            setErrorMsg({
                invoice_date_error: "Invoice date can not be empty",
            });
        } else if (!invoiceDetails.supplier_bill_image || invoiceDetails.supplier_bill_image?.length == 0) {
            setShowError(true);
            setErrorMsg({
                invoice_image_error: "Supplier bill image is missing",
            });
            setShowToast(false);
            setShowToast(true);
        } else {
            setShowError(false);
            setErrorMsg("");
            setShowToast(false);
            displayInvoicePdf();
            setShowPDF(true);
        }
    };

    const displayInvoicePdf = (blob) => {
        if (blob) {
            setPdfBlob(blob);
        } else {
            fetch(invoiceDetails.supplier_bill_image)
                .then((resp) => resp.arrayBuffer())
                .then((buffer) => {
                    const blob = new Blob([buffer], { type: "application/pdf" });
                    setPdfBlob(blob);
                });
        }
    };

    const userId = window.localStorage.getItem(LS_KEYS.USER_ID);
    const meta = userId ? { "x-amz-meta-user-id": userId } : {};

    return {
        po,
        items,
        showLoader,
        tonnage,
        showError,
        invoiceDetails,
        errorMsg,
        showToast,
        showPDF,
        pdfBlob,
        setPdfBlob,
        showFile,
        setShowFile,
        showImages,
        setShowImages,
        imageArray,
        setImageArray,
        pngArray,
        setPngArray,
        formData,
        setFormData,
        todayDate,
        showPopup,
        setShowPopup,
        useSellerAddress,
        setUseSellerAddress,
        setInvoiceDetails,
        cities,
        meta,
        closePopupHandler,
        setUpdateInvoiceDetails,
        wrapperToNavigateToInvoicePdf,
        // reupload flow is not used in the route entry (DDPoInvoiceFormView),
        // but we expose pageSource as "form" to keep the view prop surface consistent
        pageSource: "form",
        poNumber: null,
        presignedUrl: null,
        documentPath: null,
        navigate,
        purchaseOrderId,
        source,
    };
}
